import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { fmtMoney, fmtNumber } from '../lib/format';
import type { MarginLevel } from '../lib/types';
import { Button, Callout, Checkbox, Dialog, Input, Ltr, Select, Textarea, useToast } from './ui';
import { MarginBadge } from './Margin';
import { ProductPicker } from './ProductPicker';
import { IconUpload, IconX } from './icons';
import { useReaderStatus } from './ReaderSettings';

type ReadProduct = { productId: number; sku: string; name: string; unit: string | null; unitsPerPack: number | null; price: number | null; source: string; margin?: number | null; marginLevel?: MarginLevel | null };
type ReadLine = {
  index: number;
  text: string;
  quantity: number | null;
  unit: string | null;
  note: string | null;
  productId: number | null;
  productQuantity: number | null;
  confidence: number;
  source: 'learned' | 'ai' | 'local' | null;
  reason: string | null;
  candidates: number[];
};
type ReadResult = { runId: number; engine: 'ai' | 'local'; lines: ReadLine[]; products: Record<number, ReadProduct> };
type Row = { line: ReadLine; productId: number | null; extra: ReadProduct | null; quantity: string; price: string; priceEdited: boolean; include: boolean; free: string };

/** A picture → a smaller JPEG (long side 2000px): faster, cheaper, within the AI's image limits. */
export async function shrinkImage(file: File): Promise<Blob> {
  if (file.type === 'application/pdf') return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 3_000_000) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b ?? file), 'image/jpeg', 0.88));
}

const SOURCE_LABEL = { learned: 'נלמד', ai: 'הסוכן', local: 'לפי מילים' } as const;

/**
 * "הצעת מחיר מרשימה": paste a picture / PDF / text of the customer's list → each line matched to the
 * catalog at the customer's price → the user confirms → a draft quote. Every confirmation is learned.
 */
export function ListReaderDialog({ customerId, onClose }: { customerId: number; onClose: () => void }) {
  const status = useReaderStatus();
  const can = useCan();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'read' | 'confirm' | null>(null);
  const [result, setResult] = useState<ReadResult | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [docType, setDocType] = useState<'quote' | 'delivery_note' | 'tax_invoice' | 'tax_invoice_receipt'>('quote');
  const [searching, setSearching] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const connected = Boolean(status.data?.connected);

  const take = (f: File) => {
    setFile(f);
    setPreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };
  // Ctrl+V anywhere in the dialog: a picture or text.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (result) return;
      const img = [...(e.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/') || f.type === 'application/pdf');
      if (img) {
        e.preventDefault();
        take(img);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [result]);
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);

  const read = async () => {
    setBusy('read');
    try {
      const form = new FormData();
      form.append('customerId', String(customerId));
      if (file) form.append('file', await shrinkImage(file), file.type === 'application/pdf' ? file.name : 'list.jpg');
      else form.append('text', text);
      const r = await api<ReadResult>('/api/reader/read', { form });
      setResult(r);
      setRows(
        r.lines.map((l) => {
          const p = l.productId ? r.products[l.productId] : null;
          return { line: l, productId: l.productId, extra: null, quantity: String(l.productQuantity ?? l.quantity ?? 1), price: p?.price != null ? String(p.price) : '', priceEdited: false, include: true, free: l.text };
        }),
      );
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  const productOf = (r: Row) => (r.productId ? (result?.products[r.productId] ?? (r.extra?.productId === r.productId ? r.extra : null)) : null);
  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const choose = (i: number, productId: number | null, extra: ReadProduct | null = null) => {
    const p = productId ? (result?.products[productId] ?? extra) : null;
    setRow(i, { productId, extra, price: p?.price != null ? String(p.price) : '', priceEdited: false });
  };

  const included = rows.filter((r) => r.include);
  const unsure = included.filter((r) => !r.productId || (r.line.source !== 'learned' && r.line.confidence < 0.7 && r.productId === r.line.productId));
  const total = included.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.price) || 0), 0);

  const confirm = async () => {
    setBusy('confirm');
    try {
      const lines = included.map((r) => ({
        index: r.line.index,
        productId: r.productId,
        description: r.free.trim() || r.line.text,
        quantity: Number(r.quantity) > 0 ? Number(r.quantity) : 1,
        // the customer's price unless typed here
        price: r.priceEdited && r.price.trim() !== '' ? Number(r.price) : r.productId ? null : Number(r.price) || 0,
        learn: true,
      }));
      const { orderId } = await api<{ orderId: number }>(`/api/reader/runs/${result!.runId}/confirm`, { body: { documentType: docType, lines } });
      qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('נפתחה טיוטה — ההתאמות נשמרו ללמידה');
      navigate(`/orders/${orderId}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="הצעת מחיר מרשימה"
      size="lg"
      footer={
        result ? (
          <>
            <Button variant="primary" onClick={confirm} loading={busy === 'confirm'} disabled={!included.length}>
              {docType === 'quote' ? 'צור הצעת מחיר' : docType === 'delivery_note' ? 'צור תעודת משלוח' : docType === 'tax_invoice_receipt' ? 'צור חשבונית מס/קבלה' : 'צור חשבונית'} ({fmtNumber(included.length)} שורות)
            </Button>
            <Select value={docType} onChange={(e) => setDocType(e.target.value as typeof docType)} style={{ width: 'auto' }} aria-label="סוג מסמך">
              <option value="quote">הצעת מחיר</option>
              <option value="delivery_note">תעודת משלוח</option>
              <option value="tax_invoice">חשבונית מס</option>
              <option value="tax_invoice_receipt">חשבונית מס/קבלה</option>
            </Select>
            <Button variant="ghost" onClick={() => (setResult(null), setRows([]))}>רשימה אחרת</Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={read} loading={busy === 'read'} disabled={!file && !text.trim()}>
              {busy === 'read' ? 'הסוכן קורא…' : 'קריאה והתאמה'}
            </Button>
            <Button onClick={onClose}>ביטול</Button>
          </>
        )
      }
    >
      {!result ? (
        <div className="stack">
          {!connected && status.data ? (
            <Callout tone="info">
              סוכן הקריאה עוד לא מחובר — אפשר להדביק את הרשימה כטקסט (התאמה בסיסית). לקריאת תמונות ו-PDF מחברים מפתח בהגדרות ← "סוכן קריאה".
            </Callout>
          ) : null}
          <div
            className="dropzone"
            role="button"
            tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) take(f);
            }}
          >
            {file ? (
              <div className="stack" style={{ alignItems: 'center', gap: 6 }}>
                {preview ? <img src={preview} alt="הרשימה" style={{ maxHeight: 260, maxWidth: '100%', borderRadius: 6 }} /> : <div className="strong">{file.name}</div>}
                <Button size="sm" variant="ghost" onClick={(e) => (e.stopPropagation(), setFile(null), setPreview(null))}><IconX size={14} /> הסרה</Button>
              </div>
            ) : (
              <>
                <IconUpload size={22} />
                <div className="strong" style={{ marginTop: 8 }}>מדביקים תמונה (Ctrl+V), גוררים קובץ או לוחצים לבחירה</div>
                <div className="small muted">צילום של רשימה בכתב יד, צילום מסך מוואטסאפ, טבלה או PDF</div>
              </>
            )}
          </div>
          <input ref={input} type="file" hidden accept="image/*,application/pdf" onChange={(e) => (e.target.files?.[0] ? take(e.target.files[0]) : null, (e.target.value = ''))} />
          {!file ? (
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={'או מדביקים כאן טקסט מוואטסאפ / מייל, שורה לכל מוצר:\n3 ארגזים נייר A4\nנוזל כלים טאץ x4'} aria-label="טקסט הרשימה" />
          ) : null}
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          <div className="row wrap" style={{ gap: 8 }}>
            <span className="badge ok">{fmtNumber(included.length - unsure.length)} מותאמים</span>
            {unsure.length ? <span className="badge warn">{fmtNumber(unsure.length)} לבדיקה</span> : null}
            <span className="small muted">{result.engine === 'ai' ? 'נקרא על ידי הסוכן' : 'התאמה לפי מילים (בלי סוכן)'} · המחירים של הלקוח, לפני מע״מ</span>
            <span className="spacer" />
            <span className="strong tabular">סה״כ {fmtMoney(Math.round(total * 100) / 100)}</span>
          </div>
          <div className="table-wrap" style={{ maxHeight: '58vh', overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th>הלקוח כתב</th>
                  <th>מוצר בקטלוג</th>
                  <th className="num" style={{ width: 80 }}>כמות</th>
                  <th className="num" style={{ width: 100 }}>מחיר</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const p = productOf(r);
                  const sure = r.productId && (r.line.source === 'learned' || r.line.confidence >= 0.7 || r.productId !== r.line.productId);
                  return (
                    <tr key={r.line.index} style={!r.include ? { opacity: 0.45 } : !sure ? { background: 'var(--warn-soft)' } : undefined}>
                      <td><Checkbox checked={r.include} onChange={(v) => setRow(i, { include: v })}><span className="sr-only">לכלול</span></Checkbox></td>
                      <td style={{ maxWidth: 200 }}>
                        <span className="strong">{r.line.text}</span>
                        <span className="sub">
                          {r.line.quantity != null ? `${fmtNumber(r.line.quantity)} ${r.line.unit ?? ''}` : r.line.unit ?? ''}
                          {r.line.note ? ` · ${r.line.note}` : ''}
                        </span>
                      </td>
                      <td>
                        <Select
                          value={r.productId ?? ''}
                          onChange={(e) => (e.target.value === 'search' ? setSearching(i) : choose(i, e.target.value ? Number(e.target.value) : null))}
                          aria-label="מוצר"
                          disabled={!r.include}
                        >
                          {r.line.candidates.map((id) => {
                            const c = result.products[id];
                            return c ? <option key={id} value={id}>{c.name}{c.price != null ? ` — ${fmtMoney(c.price)}` : ''}</option> : null;
                          })}
                          {r.extra && !r.line.candidates.includes(r.extra.productId) ? <option value={r.extra.productId}>{r.extra.name}</option> : null}
                          <option value="">— שורה חופשית (לא מהקטלוג) —</option>
                          {can('catalog.edit') ? <option value="search">חיפוש מוצר אחר…</option> : null}
                        </Select>
                        <span className="sub">
                          {p ? <><Ltr>{p.sku}</Ltr>{p.unitsPerPack ? ` · ${p.unitsPerPack} ${/-box$/i.test(p.sku) ? 'חב׳ בארגז' : 'יח׳ באריזה'}` : ''} · </> : null}
                          {r.productId && r.productId === r.line.productId && r.line.source ? (
                            <span style={{ color: sure ? 'var(--ok)' : 'var(--warn)' }}>{SOURCE_LABEL[r.line.source]}{r.line.source !== 'learned' ? ` ${Math.round(r.line.confidence * 100)}%` : ''}</span>
                          ) : !r.productId ? <span style={{ color: 'var(--warn)' }}>לא נמצא בקטלוג</span> : <span>נבחר ידנית</span>}
                          {r.line.reason ? ` · ${r.line.reason}` : ''}
                        </span>
                        {!r.productId ? <Input value={r.free} onChange={(e) => setRow(i, { free: e.target.value })} aria-label="תיאור השורה" style={{ marginTop: 4 }} /> : null}
                        {searching === i ? (
                          <div style={{ marginTop: 4 }}>
                            <ProductPicker
                              autoFocus
                              onPick={(x) => {
                                choose(i, x.id, { productId: x.id, sku: x.sku, name: x.name, unit: null, unitsPerPack: null, price: null, source: 'base' });
                                setSearching(null);
                              }}
                            />
                          </div>
                        ) : null}
                      </td>
                      <td className="num"><Input className="qty-input" inputMode="decimal" value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} aria-label="כמות" disabled={!r.include} /></td>
                      <td className="num">
                        <Input className="price-input" inputMode="decimal" value={r.price} placeholder={r.productId ? 'מחיר הלקוח' : '0'} onChange={(e) => setRow(i, { price: e.target.value, priceEdited: true })} aria-label="מחיר" disabled={!r.include} />
                        {p && 'margin' in p && !r.priceEdited ? <span className="sub"><MarginBadge margin={p.margin} level={p.marginLevel} /></span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            מה שמסומן בצהוב — לבדוק. כל התאמה שתאשר נשמרת, ובפעם הבאה אותו ניסוח יותאם לבד. בטיוטה אפשר עוד לשנות הכול לפני ההפקה.
          </p>
        </div>
      )}
    </Dialog>
  );
}
